<template>
  <div class="overflow-x-auto -mx-4 sm:mx-0">
    <div class="inline-block min-w-full px-4 sm:px-0">
      <div class="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                class="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{ $t('app.views.orders.modals.details.headers.item') }}
              </th>
              <th
                scope="col"
                class="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{ $t('app.views.orders.modals.details.headers.qty') }}
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{ $t('app.views.orders.modals.details.headers.price') }}
              </th>
              <th
                scope="col"
                class="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{ $t('app.views.orders.modals.details.headers.total') }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            <tr
              v-for="item in items"
              :key="item.id"
            >
              <td class="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-900 dark:text-gray-100">
                <div class="font-medium text-gray-900 dark:text-gray-100 text-xs sm:text-sm">
                  {{
                    getItemName(item) }}
                </div>
                <div
                  v-if="item.variant"
                  class="text-xs text-gray-600 dark:text-gray-400 mt-0.5"
                >
                  {{ item.variant.name }}
                </div>
                <div
                  v-if="item.menu_item?.category"
                  class="mt-1 flex items-center gap-1.5 flex-wrap"
                >
                  <span
                    class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
                  >
                    {{ getCategoryName(item.menu_item.category) }}
                  </span>
                </div>
                <!-- Item Type Badge (only for takeaway items when order is dine_in) -->
                <div
                  v-if="item.item_type === 'takeaway' && order.order_type === 'dine_in'"
                  class="mt-1"
                >
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                  >
                    Para llevar
                  </span>
                </div>
                <div
                  v-if="!isPosOnlyMode && getItemNotes(item)"
                  class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                >
                  <span class="font-medium">{{ $t('app.views.orders.modals.details.note') }}</span> {{
                    getItemNotes(item) }}
                </div>
                <!-- Show extras if any -->
                <div
                  v-if="item.extras && item.extras.length > 0"
                  class="mt-1 space-y-0.5"
                >
                  <p
                    v-for="(extra, idx) in item.extras"
                    :key="idx"
                    class="text-xs text-indigo-600 dark:text-indigo-400"
                  >
                    + {{ extra.name }} ({{ extra.quantity }}x ${{ extra.price.toFixed(2) }})
                  </p>
                </div>
                <!-- Show price on mobile (hidden on desktop) -->
                <div class="sm:hidden text-xs text-gray-500 dark:text-gray-400 mt-1">
                  ${{ (item.unit_price || 0).toFixed(2) }} c/u
                </div>
              </td>
              <td
                class="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right align-top"
              >
                {{ item.quantity }}
              </td>
              <td
                class="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-sm text-right align-top"
              >
                <div class="flex items-center justify-end gap-2">
                  <!-- Show original price if unit_price is less than menu item price (discount was applied) -->
                  <span
                    v-if="item.menu_item?.price && item.unit_price < item.menu_item.price"
                    class="text-gray-500 dark:text-gray-400 line-through text-xs"
                  >
                    ${{ (item.menu_item.price || 0).toFixed(2) }}
                  </span>
                  <span
                    :class="item.menu_item?.price && item.unit_price < item.menu_item.price ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'"
                  >
                    ${{ (item.unit_price || 0).toFixed(2) }}
                  </span>
                  <span
                    v-if="item.menu_item?.price && item.unit_price < item.menu_item.price"
                    class="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200"
                  >
                    {{ $t('app.forms.sale_badge') }}
                  </span>
                </div>
              </td>
              <td
                class="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 text-right align-top"
              >
                ${{ calculateItemTotal(item).toFixed(2) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import { useOperationMode } from '@/composables/useOperationMode'

interface Props {
  items: any[]
  order: {
    order_type?: string
  }
}

const props = defineProps<Props>()
const { t } = useI18n()
const { isPosOnlyMode } = useOperationMode()

const getItemName = (item: any) => {
  return item.menu_item?.name || item.name || 'Unknown'
}

const getCategoryName = (category: any) => {
  return typeof category === 'string' ? category : category?.name || 'Unknown'
}

const getCategoryVisibility = (item: any): boolean | null => {
  if (!item.menu_item?.category) return null;

  // If category is an object with visible_in_kitchen property
  if (typeof item.menu_item.category === 'object' && item.menu_item.category !== null) {
    return item.menu_item.category.visible_in_kitchen ?? true;
  }

  // If category is a string, assume it's visible (legacy behavior)
  return true;
}

const getItemNotes = (item: any) => {
  return item.notes || item.special_instructions || ''
}

const calculateItemTotal = (item: any) => {
  let total = item.quantity * (item.price || item.unit_price || 0);

  // Add extras to total
  if (item.extras && item.extras.length > 0) {
    const extrasTotal = item.extras.reduce((sum: number, extra: any) => {
      return sum + (extra.price * extra.quantity);
    }, 0);
    total += extrasTotal;
  }

  return total;
}
</script>
